// frontend/src/pages/HomePage.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowRight,
  AlertTriangle,
  FileText,
  Loader2,
  Mail,
  ShieldCheck, // Example icon
  TrendingUp, // Example icon
} from "lucide-react";
import HeroSection from "../components/sections/HeroSection"; // HeroSection handles its own dynamic content

import { getArticles } from "@/api/articleService";
import { getResearchItems } from "@/api/researchService";
import { getProducts } from "@/api/productService";

import {
  type Article as ArticleListedType,
  type ResearchListed as ResearchListedType,
  type ProductListed,
  type HomepageValuePropCard as ValuePropCardType, // --- FIX: Use the imported type ---
} from "@/types";
import { useSiteSettingsStore } from "@/store/settingsStore";

// Helper to map string icon names to Lucide components for Value Prop Cards
const iconMap: { [key: string]: React.ElementType } = {
  FileText,
  ShieldCheck,
  TrendingUp,
  // Add other icon keys here as they are defined in your CMS/settings
};

const ValuePropIcon = ({ name }: { name: string | undefined }) => {
  if (!name) return null;
  const IconComponent = iconMap[name];
  return IconComponent ? <IconComponent className="h-10 w-10 text-primary" /> : null;
};

const HomePage = () => {
  const navigate = useNavigate();

  const publicSettings = useSiteSettingsStore((state) => state.publicSettings);
  const isLoadingSettingsGlobal = useSiteSettingsStore(
    (state) => state.isLoading
  );
  
  // State for parsed Value Proposition Cards
  const [valuePropCards, setValuePropCards] = useState<ValuePropCardType[]>([]);


  // Page visibility settings
  const articlesPageEnabled = isLoadingSettingsGlobal
    ? true
    : (publicSettings?.page_articles_enabled ?? true);
  const researchPageEnabled = isLoadingSettingsGlobal
    ? true
    : (publicSettings?.page_research_enabled ?? true);
  const productsPageEnabled = isLoadingSettingsGlobal
    ? true
    : (publicSettings?.page_products_enabled ?? true);
  const aboutPageEnabled = isLoadingSettingsGlobal
    ? true
    : (publicSettings?.page_about_enabled ?? true);
  const contactPageEnabled = isLoadingSettingsGlobal
    ? true
    : (publicSettings?.page_contact_enabled ?? true);

  // Dynamic Homepage Content
  const homeCtaTitle = isLoadingSettingsGlobal
    ? "Loading..."
    : publicSettings?.home_cta_title ||
      "Ready to Elevate Your Financial Strategy?";
  const homeCtaSubtitle = isLoadingSettingsGlobal
    ? "Loading..."
    : publicSettings?.home_cta_subtitle ||
      "Join Vedas Trading and gain access to a wealth of knowledge, powerful tools, and a community dedicated to financial excellence.";

  // State for fetched content
  const [featuredArticlesData, setFeaturedArticlesData] = useState<
    ArticleListedType[]
  >([]);
  const [featuredResearchData, setFeaturedResearchData] = useState<
    ResearchListedType[]
  >([]);
  const [productsOverviewData, setProductsOverviewData] = useState<
    ProductListed[]
  >([]);

  const [isLoadingPageSpecificContent, setIsLoadingPageSpecificContent] =
    useState(true);
  const [error, setError] = useState<string | null>(null);

  const backendBaseUrl =
    import.meta.env.VITE_API_BASE_URL?.replace("/api/v1", "") ||
    "http://localhost:8000";
    
  useEffect(() => {
    // The data from the store is already an array of objects, not a JSON string.
    // No parsing is needed.
    if (publicSettings?.home_value_prop_cards_json && Array.isArray(publicSettings.home_value_prop_cards_json)) {
      setValuePropCards(publicSettings.home_value_prop_cards_json);
    } else {
      // Set to empty array if the data is not available or not in the expected array format.
      setValuePropCards([]);
    }
  }, [publicSettings]);


  const getProductImageUrl = (
    imagePath?: string | null
  ): string | undefined => {
    if (!imagePath) return undefined;
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://"))
      return imagePath;
    const cleanBaseUrl = backendBaseUrl.endsWith("/")
      ? backendBaseUrl.slice(0, -1)
      : backendBaseUrl;
    const pathPrefix = cleanBaseUrl + "/static/";

    if (imagePath.startsWith("/static/")) return `${cleanBaseUrl}${imagePath}`;
    if (imagePath.startsWith("static/")) return `${cleanBaseUrl}/${imagePath}`;

    const relativePathCleaned = imagePath.startsWith("/")
      ? imagePath.substring(1)
      : imagePath;
    return `${pathPrefix}${relativePathCleaned}`;
  };

  const getFullPdfUrl = (pdfPath?: string | null): string => {
    if (!pdfPath) return "#";
    if (pdfPath.startsWith("http://") || pdfPath.startsWith("https://"))
      return pdfPath;
    const cleanBaseUrl = backendBaseUrl.endsWith("/")
      ? backendBaseUrl.slice(0, -1)
      : backendBaseUrl;
    const pathPrefix = cleanBaseUrl + "/static/";

    if (pdfPath.startsWith("/static/")) return `${cleanBaseUrl}${pdfPath}`;
    if (pdfPath.startsWith("static/")) return `${cleanBaseUrl}/${pdfPath}`;

    const relativePathCleaned = pdfPath.startsWith("/")
      ? pdfPath.substring(1)
      : pdfPath;
    return `${pathPrefix}${relativePathCleaned}`;
  };

  useEffect(() => {
    const fetchHomePageData = async () => {
      setIsLoadingPageSpecificContent(true);
      setError(null);
      try {
        const promises = [
          articlesPageEnabled
            ? getArticles({ limit: 3, status: "published" })
            : Promise.resolve([]),
          researchPageEnabled
            ? getResearchItems({ limit: 2, status: "published" })
            : Promise.resolve([]),
          productsPageEnabled
            ? getProducts({ limit: 2, status: "active" })
            : Promise.resolve([]),
        ] as const;

        const [articlesRes, researchRes, productsRes] = await Promise.all(promises);

        // --- FIX: Use Array.isArray for a more robust check. This prevents crashes if the API returns an object instead of an array. ---
        setFeaturedArticlesData(Array.isArray(articlesRes) ? articlesRes : []);
        setFeaturedResearchData(Array.isArray(researchRes) ? researchRes : []);
        setProductsOverviewData(Array.isArray(productsRes) ? productsRes : []);

      } catch (err) {
        console.error("Failed to fetch homepage data sections:", err);
        setError(
          "Could not load all featured content sections. Some sections may be unavailable."
        );
      } finally {
        setIsLoadingPageSpecificContent(false);
      }
    };
    if (!isLoadingSettingsGlobal) {
      fetchHomePageData();
    }
  }, [
    articlesPageEnabled,
    researchPageEnabled,
    productsPageEnabled,
    isLoadingSettingsGlobal,
  ]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoadingSettingsGlobal && !publicSettings) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-16 md:space-y-24 pb-16 text-foreground bg-background">
      <HeroSection />
      {/* Value Proposition Section - Dynamic Content */}
      <section className="container mx-auto px-4">
        {isLoadingSettingsGlobal && valuePropCards.length === 0 ? (
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="p-6 rounded-lg bg-card border border-border/60 shadow-lg"
              >
                <div className="mb-4 inline-block p-3 rounded-full bg-muted/30 animate-pulse h-16 w-16"></div>
                <div className="h-6 bg-muted rounded animate-pulse w-3/4 mx-auto mb-2"></div>
                <div className="h-4 bg-muted rounded animate-pulse w-full mb-1"></div>
                <div className="h-4 bg-muted rounded animate-pulse w-5/6 mx-auto"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {/* Check for valuePropCards length before mapping */}
            {valuePropCards && valuePropCards.length > 0 && valuePropCards.map((card, index) => (
              <Card
                key={index}
                className="p-6 rounded-lg bg-card border border-border/60 shadow-lg text-center"
              >
                <div className="mb-4 inline-block p-3 rounded-full bg-primary/10 dark:bg-accent/10">
                  <ValuePropIcon name={card.icon} />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  {card.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {card.description}
                </p>
              </Card>
            ))}
          </div>
        )}
      </section>
      
      {error && (
        <section className="container mx-auto px-4">
          <div className="p-4 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 mr-2" /> {error}
          </div>
        </section>
      )}
      
      {/* Featured Articles Section */}
      {articlesPageEnabled && (
        <section className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl md:text-4xl font-semibold text-gradient-maroon-to-gold">
              Latest Articles & Insights
            </h2>
            <Button
              variant="outline"
              onClick={() => navigate("/articles")}
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground dark:border-accent dark:text-accent dark:hover:bg-accent dark:hover:text-accent-foreground !cursor-pointer"
            >
              View All Articles <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {isLoadingPageSpecificContent ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="bg-card p-4">
                  <div className="h-48 bg-muted rounded animate-pulse"></div>
                </Card>
              ))}
            </div>
          ) : featuredArticlesData && featuredArticlesData.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {featuredArticlesData.map((article) => (
                <Card
                  key={article.id}
                  className="group flex flex-col bg-card text-card-foreground transform hover:scale-[1.03] transition-all duration-300 ease-out shadow-lg hover:shadow-xl border border-border/60 hover:border-primary/40 dark:border-border dark:hover:border-primary/30 relative overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 dark:from-primary/10 dark:via-transparent dark:to-accent/10"></div>
                  <div className="relative z-10 flex flex-col flex-grow h-full">
                    <CardHeader>
                      <CardTitle className="text-xl text-card-foreground group-hover:text-primary transition-colors duration-200 dark:group-hover:text-accent">
                        <Link to={`/articles/${article.slug}`}>
                          {article.title}
                        </Link>
                      </CardTitle>
                      <CardDescription className="text-sm pt-1 text-muted-foreground">
                        Published on {formatDate(article.published_at)}
                        {article.author_name && ` by ${article.author_name}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <p className="text-muted-foreground text-sm line-clamp-3">
                        {article.summary}
                      </p>
                    </CardContent>
                    <div className="p-6 pt-0">
                      <Button
                        variant="link"
                        onClick={() => navigate(`/articles/${article.slug}`)}
                        className="p-0 text-primary group-hover:text-accent transition-colors duration-200 dark:text-accent dark:group-hover:text-primary !cursor-pointer"
                      >
                        Read More <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No featured articles available at the moment.
            </p>
          )}
        </section>
      )}

      {/* Key Research Papers Section */}
      {researchPageEnabled && (
        <section className="container mx-auto px-4 py-12 my-8 rounded-xl shadow-2xl bg-deep-maroon-gradient">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 sm:mb-0">
              Key Research Papers
            </h2>
            <Button
              variant="outline"
              onClick={() => navigate("/research")}
              className="border-accent text-accent hover:bg-accent hover:text-accent-foreground dark:border-accent dark:text-accent dark:hover:bg-accent dark:hover:text-accent-foreground transition-colors duration-300 !cursor-pointer"
            >
              Explore All Research <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {isLoadingPageSpecificContent ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[...Array(2)].map((_, i) => (
                <Card
                  key={i}
                  className="!bg-white/90 dark:!bg-neutral-800/90 p-4"
                >
                  <div className="h-40 bg-muted rounded animate-pulse"></div>
                </Card>
              ))}
            </div>
          ) : featuredResearchData && featuredResearchData.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {featuredResearchData.map((item) => (
                <Card
                  key={item.id}
                  className="group flex flex-col !bg-white dark:bg-neutral-800 shadow-xl hover:shadow-2xl rounded-lg transform hover:-translate-y-1 transition-all duration-300 ease-out border border-transparent hover:border-accent/50"
                >
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl lg:text-2xl font-semibold text-primary dark:text-accent group-hover:text-red-700 dark:group-hover:text-yellow-400 transition-colors duration-200">
                      <a
                        href={getFullPdfUrl(item.pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.title}
                      </a>
                    </CardTitle>
                    <CardDescription className="text-xs text-neutral-500 dark:text-neutral-400 pt-1">
                      Published on{" "}
                      {formatDate(item.published_at || item.upload_date)}
                      {item.uploader_name && ` by ${item.uploader_name}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="!text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed line-clamp-3">
                      {item.description}
                    </p>
                  </CardContent>
                  <div className="p-6 pt-4">
                    <Button
                      variant="default"
                      onClick={() =>
                        window.open(getFullPdfUrl(item.pdf_url), "_blank")
                      }
                      className="!bg-primary hover:!bg-red-700 !text-primary-foreground dark:!bg-accent dark:hover:!bg-yellow-500 dark:!text-accent-foreground shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto !cursor-pointer"
                    >
                      Download PDF <FileText className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-white/80 text-center py-8">
              No featured research papers available at the moment.
            </p>
          )}
        </section>
      )}

      {/* Discover Our Products Section */}
      {productsPageEnabled && (
        <section className="container mx-auto px-4 py-12 bg-gradient-to-tr from-muted/50 via-background to-muted/30 dark:from-muted/20 dark:via-background dark:to-muted/10 rounded-xl shadow-inner border border-border/20">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-gradient-maroon-to-gold">
              Discover Our Products
            </h2>
            <p className="mt-2 text-lg text-muted-foreground max-w-xl mx-auto">
              Tailored solutions to meet the demands of modern financial
              markets.
            </p>
          </div>
          {isLoadingPageSpecificContent ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[...Array(2)].map((_, i) => (
                <Card key={i} className="bg-card p-4">
                  <div className="h-32 bg-muted rounded animate-pulse"></div>
                </Card>
              ))}
            </div>
          ) : productsOverviewData && productsOverviewData.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {productsOverviewData.map((product) => {
                const imageUrl = getProductImageUrl(product.image_url);
                return (
                  <Card
                    key={product.id}
                    className="group shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-card text-card-foreground border border-border/60 hover:border-primary/40 dark:border-border dark:hover:border-primary/30 relative overflow-hidden transform hover:scale-[1.02]"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    {imageUrl && (
                      <div className="h-40 w-full overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                    )}
                    <div className="relative z-10 p-6">
                      <CardHeader className="p-0 mb-3">
                        <CardTitle className="text-2xl text-primary group-hover:text-gradient-maroon-to-gold transition-all duration-300">
                          {product.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {product.description}
                        </p>
                        <Button
                          variant="ghost"
                          onClick={() => navigate(`/products/${product.slug}`)}
                          className="p-0 text-primary hover:text-accent hover:bg-primary/5 dark:text-accent dark:hover:text-primary dark:hover:bg-accent/10 !cursor-pointer"
                        >
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </CardContent>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Product information is currently unavailable.
            </p>
          )}
        </section>
      )}
      
      {/* Call to Action Section */}
      {(aboutPageEnabled || contactPageEnabled) && (
        <section className="container mx-auto px-4 text-center py-16 my-12 rounded-xl shadow-2xl bg-section-gradient-p-a ">
          <h2 className="text-3xl font-semibold mb-4 text-primary-foreground brightness-110">
            {homeCtaTitle}
          </h2>
          <p className="text-primary-foreground max-w-xl mx-auto mb-8 brightness-110">
            {homeCtaSubtitle}
          </p>
          <div className="space-x-0 space-y-4 sm:space-y-0 sm:space-x-4 flex flex-col sm:flex-row justify-center items-center">
            {aboutPageEnabled && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate("/about")}
                className="!border-primary-foreground/70 !text-primary-foreground hover:!bg-primary-foreground/10 hover:!border-primary-foreground transition-all duration-300 !cursor-pointer w-full sm:w-auto"
              >
                About Our Company
              </Button>
            )}
            {contactPageEnabled && (
              <Button
                variant="default"
                size="lg"
                onClick={() => navigate("/contact")}
                className="bg-accent hover:bg-accent/90 text-accent-foreground transition-all duration-300 !cursor-pointer w-full sm:w-auto"
              >
                <Mail className="mr-2 h-5 w-5" /> Contact Us
              </Button>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
